/**
 * 助理設定 Modal(REQ-20260519002):
 * 從智慧客服頁直接調 AI System Prompt 與服務項目資料,
 * 兩者都寫入 line.bot setting,AI 回覆時自動帶入。
 * 與 /settings/line 共用同一份 setting;此處不顯示 token / secret 等連線欄位。
 */
import { useEffect, useState } from 'react';
import {
  LINE_DEFAULT_AI_PROMPT,
  type LineServiceItem,
  type LineSettingInput,
} from '@app/shared';
import { Modal } from '../../components/Modal';
import { useLineSetting, useUpdateLine } from '../settings/lineApi';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Draft {
  aiSystemPrompt: string;
  serviceItems: LineServiceItem[];
}

export function AssistantSettingsModal({ open, onClose }: Props): JSX.Element {
  const { data, isLoading, isError, error } = useLineSetting();
  const update = useUpdateLine();
  const [draft, setDraft] = useState<Draft>({
    aiSystemPrompt: '',
    serviceItems: [],
  });
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  // 開啟 / 設定變動時把 server 值灌進 draft
  useEffect(() => {
    if (open && data) {
      setDraft({
        aiSystemPrompt: data.aiSystemPrompt || LINE_DEFAULT_AI_PROMPT,
        serviceItems: data.serviceItems ?? [],
      });
      setSavedFlash(null);
      update.reset();
    }
    // 刻意省略部分 deps(只在指定值變動時重跑)。
    // 註:專案未安裝 react-hooks plugin,原本這裡的 disable 指令引用了不存在的
    // 規則,反而被 ESLint 判為錯誤,故移除。導入該 plugin 時要記得補回來。
  }, [open, data?.updatedAt]);

  const onSave = (): void => {
    if (!data) return;
    // 把現有設定 + 編輯後的 prompt / serviceItems 一起送出。
    // accessToken / channelSecret 留空 = 後端保留原值(同其他表單模式)。
    const payload: LineSettingInput = {
      enabled: data.enabled,
      accessToken: '',
      channelSecret: '',
      aiEnabled: data.aiEnabled,
      aiSystemPrompt: draft.aiSystemPrompt,
      serviceItems: draft.serviceItems,
    };
    update.mutate(payload, {
      onSuccess: () => setSavedFlash('已儲存'),
    });
  };

  const addItem = (): void => {
    setDraft((d) => ({
      ...d,
      serviceItems: [...d.serviceItems, { name: '', description: '' }],
    }));
  };

  const updateItem = (i: number, patch: Partial<LineServiceItem>): void => {
    setDraft((d) => ({
      ...d,
      serviceItems: d.serviceItems.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));
  };

  const removeItem = (i: number): void => {
    setDraft((d) => ({
      ...d,
      serviceItems: d.serviceItems.filter((_, idx) => idx !== i),
    }));
  };

  const moveItem = (i: number, dir: -1 | 1): void => {
    setDraft((d) => {
      const arr = d.serviceItems.slice();
      const j = i + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[i], arr[j]] = [arr[j]!, arr[i]!];
      return { ...d, serviceItems: arr };
    });
  };

  const errMsg = (e: unknown): string => (e instanceof Error ? e.message : '未知錯誤');
  const hasEmptyName = draft.serviceItems.some((it) => it.name.trim().length === 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="AI 助理設定"
      widthClass="max-w-3xl"
      dismissOnBackdrop={!update.isPending}
    >
      <div className="grid gap-4">
        {isLoading ? (
          <div className="text-sm text-slate-500">載入中...</div>
        ) : isError ? (
          <div role="alert" className="text-red-600 text-sm">載入失敗:{errMsg(error)}</div>
        ) : (
          <>
            <p className="text-xs text-slate-500">
              這裡的設定與「設定 → LINE 設定」共用同一份資料,
              AI 自動回覆時系統會把「System Prompt」與下方「服務 / 商品項目」一起餵給模型。
            </p>

            <label className="grid gap-1 text-sm">
              <span className="text-slate-700 font-medium">System Prompt</span>
              <textarea
                value={draft.aiSystemPrompt}
                onChange={(e) => setDraft((d) => ({ ...d, aiSystemPrompt: e.target.value }))}
                rows={7}
                disabled={update.isPending}
                className="border rounded px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <span className="text-[10px] text-slate-500">
                例:你是 XXX 公司的客服;不確定價格 / 交期就誠實說會幫忙轉客服。
              </span>
            </label>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">
                  服務 / 商品項目({draft.serviceItems.length})
                </span>
                <button
                  type="button"
                  onClick={addItem}
                  disabled={update.isPending || draft.serviceItems.length >= 100}
                  className="text-xs border border-blue-600 text-blue-700 px-2 py-1 rounded hover:bg-blue-50 disabled:opacity-50"
                >
                  + 新增一項
                </button>
              </div>

              {draft.serviceItems.length === 0 ? (
                <div className="text-sm text-slate-500 bg-slate-50 border rounded p-3">
                  尚未建立服務項目。
                  AI 在不知道具體服務內容時會傾向「轉客服」;
                  在這裡列出你的主要服務 / 商品 / 常見問答,可以讓 AI 更精準回應。
                </div>
              ) : (
                <ul className="grid gap-2">
                  {draft.serviceItems.map((it, i) => (
                    <li key={i} className="flex gap-2 items-start border rounded p-2 bg-white">
                      <div className="text-xs text-slate-400 pt-1.5 w-6 text-right">{i + 1}.</div>
                      <div className="flex-1 grid gap-1">
                        <input
                          value={it.name}
                          onChange={(e) => updateItem(i, { name: e.target.value })}
                          placeholder="服務名稱(必填,例如:複寫五聯訂購單)"
                          disabled={update.isPending}
                          className={
                            'border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 ' +
                            (it.name.trim().length === 0
                              ? 'border-red-300 focus:ring-red-300'
                              : 'focus:ring-blue-400')
                          }
                        />
                        <textarea
                          value={it.description}
                          onChange={(e) => updateItem(i, { description: e.target.value })}
                          rows={2}
                          placeholder="說明(可空,寫越具體 AI 越能精準引用,例如:A4 規格、白紅黃綠藍五聯、最低 1000 本)"
                          disabled={update.isPending}
                          className="border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => moveItem(i, -1)}
                          disabled={i === 0 || update.isPending}
                          className="text-xs text-slate-500 hover:text-slate-800 disabled:opacity-30"
                          title="往上"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItem(i, 1)}
                          disabled={i === draft.serviceItems.length - 1 || update.isPending}
                          className="text-xs text-slate-500 hover:text-slate-800 disabled:opacity-30"
                          title="往下"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(i)}
                          disabled={update.isPending}
                          className="text-xs text-red-500 hover:text-red-700"
                          title="刪除"
                        >
                          ×
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {update.isError ? (
              <div role="alert" className="text-red-600 text-sm">
                儲存失敗:{errMsg(update.error)}
              </div>
            ) : null}
            {savedFlash ? (
              <div role="status" className="text-green-700 text-sm">{savedFlash}</div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={update.isPending}
                className="text-sm border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-100"
              >
                關閉
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={update.isPending || hasEmptyName}
                title={hasEmptyName ? '請先填寫所有項目的名稱,或刪除空白項目' : ''}
                className="text-sm bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
              >
                {update.isPending ? '儲存中…' : '儲存'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
